import { useState } from "react";
import {
  Search,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Globe,
  BookOpen,
} from "lucide-react";
import { motion } from "motion/react";
import { useNavigate } from "react-router";
import heroImage from "../../assets/hero_image.png";

const suggestions = [
  "Business",
  "Computer Science & AI",
  "Medicine & Health Sciences",
  "Law",
  "Engineering",
  "Design / Creative",
  "Psychology",
  "Mass Communication",
  "Not sure yet",
];

const stats = [
  {
    value: "4K+",
    label: "Students Placed",
    color: "text-violet-600",
  },
  {
    value: "300+",
    label: "Partner Universities",
    color: "text-orange-500",
  },
  {
    value: "100%",
    label: "Free Applications",
    color: "text-cyan-500",
  },
  { value: "40+", label: "Countries", color: "text-lime-500" },
];

const floatingBadges = [
  {
    icon: <Sparkles className="w-3.5 h-3.5" />,
    text: "100% Scholarship Available",
    color: "bg-violet-50 text-violet-700 border-violet-100",
    position: "top-8 -right-4 lg:right-4",
  },
  {
    icon: <Globe className="w-3.5 h-3.5" />,
    text: "Study in 40+ Countries",
    color: "bg-orange-50 text-orange-700 border-orange-100",
    position: "bottom-24 -left-4 lg:left-0",
  },
  {
    icon: <TrendingUp className="w-3.5 h-3.5" />,
    text: "Personalized Recommendations",
    color: "bg-cyan-50 text-cyan-700 border-cyan-100",
    position: "bottom-4 right-0 lg:right-8",
  },
];

// Abstract Blob Shapes
function BlobOne() {
  return (
    <svg
      viewBox="0 0 600 600"
      className="absolute -top-40 -right-40 w-[600px] h-[600px] opacity-[0.07] pointer-events-none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#7C3AED"
        d="M421.5,298Q398,346,360.5,383.5Q323,421,268,424Q213,427,169,402Q125,377,97,333Q69,289,70.5,236Q72,183,103.5,143Q135,103,184.5,82.5Q234,62,283.5,62Q333,62,376,88Q419,114,432,162Q445,210,421.5,298Z"
      />
    </svg>
  );
}

function BlobTwo() {
  return (
    <svg
      viewBox="0 0 600 600"
      className="absolute -bottom-60 -left-40 w-[500px] h-[500px] opacity-[0.06] pointer-events-none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#F97316"
        d="M434,290Q420,330,394,363.5Q368,397,329,415.5Q290,434,247,427Q204,420,166,399Q128,378,106,340Q84,302,84,258Q84,214,108,178Q132,142,166,116Q200,90,244.5,77Q289,64,330,78Q371,92,401,120Q431,148,445,194Q459,240,434,290Z"
      />
    </svg>
  );
}

function BlobThree() {
  return (
    <svg
      viewBox="0 0 600 600"
      className="absolute top-40 left-20 w-[300px] h-[300px] opacity-[0.05] pointer-events-none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#06B6D4"
        d="M421.5,298Q398,346,360.5,383.5Q323,421,268,424Q213,427,169,402Q125,377,97,333Q69,289,70.5,236Q72,183,103.5,143Q135,103,184.5,82.5Q234,62,283.5,62Q333,62,376,88Q419,114,432,162Q445,210,421.5,298Z"
      />
    </svg>
  );
}

export function HeroSection() {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState<
    string | null
  >(null);
  const navigate = useNavigate();

  const filtered = suggestions.filter((s) =>
    query
      ? s.toLowerCase().includes(query.toLowerCase())
      : true,
  );

  return (
    <section className="relative min-h-screen bg-[#FAFAFA] overflow-hidden flex flex-col justify-center pt-16">
      <BlobOne />
      <BlobTwo />
      <BlobThree />

      {/* Dot grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, #d4d4d8 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
          opacity: 0.4,
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 40%, black 40%, transparent 100%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full py-20 lg:py-0 lg:min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full">
          {/* Left Column */}
          <div className="relative z-10">
            {/* Eyebrow badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 border border-violet-100 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
              <span
                className="text-[12px] text-violet-700"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                }}
              >
                Malaysia's #1 Education Consultancy
              </span>
            </motion.div>

            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h1
                className="text-[48px] sm:text-[56px] lg:text-[64px] xl:text-[72px] leading-[1.02] tracking-[-2px] text-slate-900 mb-6"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 900,
                }}
              >
                Your dream{" "}
                <span
                  style={{
                    background:
                      "linear-gradient(135deg, #7C3AED 0%, #06B6D4 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  university.
                </span>
                <br />
                <span className="text-slate-900">
                  Zero Stress.
                </span>
              </h1>
            </motion.div>

            {/* Sub */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-[16px] lg:text-[18px] text-slate-500 mb-10 max-w-lg leading-relaxed"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
              }}
            >
              One profile. Free applications to 300+
              universities — local and overseas. We handle
              everything from shortlisting to scholarship
              hunting.
            </motion.p>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="relative mb-10"
            >
              <div
                className={`flex items-center gap-3 bg-white rounded-2xl border-2 transition-all duration-300 shadow-xl shadow-slate-900/5 ${
                  focused
                    ? "border-violet-400 shadow-violet-500/10"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="pl-5">
                  <Search
                    className={`w-5 h-5 transition-colors ${
                      focused
                        ? "text-violet-500"
                        : "text-slate-400"
                    }`}
                  />
                </div>
                <input
                  type="text"
                  placeholder="What course are you interested in?"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() =>
                    setTimeout(() => setFocused(false), 150)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && query.trim())
                      navigate(
                        `/course-finder?interest=${encodeURIComponent(query.trim())}`,
                      );
                  }}
                  className="flex-1 py-4 bg-transparent outline-none text-slate-800 placeholder-slate-400 text-[15px]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                  }}
                />
                <button
                  onClick={() =>
                    navigate(
                      `/course-finder${query.trim() ? `?interest=${encodeURIComponent(query.trim())}` : ""}`,
                    )
                  }
                  className="m-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-[13px] flex items-center gap-2 hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-200 hover:-translate-y-px whitespace-nowrap cursor-pointer"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                  }}
                >
                  Find Courses{" "}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Suggestion dropdown */}
              {focused && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-100 overflow-hidden z-50">
                  <div className="p-2">
                    {filtered.map((s) => (
                      <button
                        key={s}
                        onMouseDown={() => {
                          setQuery(s);
                          setActiveSuggestion(s);
                          navigate(
                            `/course-finder?interest=${encodeURIComponent(s)}`,
                          );
                        }}
                        className="w-full text-left px-4 py-2.5 rounded-xl text-[14px] text-slate-700 hover:bg-violet-50 hover:text-violet-700 flex items-center gap-3 transition-colors"
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 400,
                        }}
                      >
                        <BookOpen className="w-4 h-4 text-slate-400" />
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-4"
            >
              {stats.map((stat, i) => (
                <div key={i} className="flex flex-col gap-0.5">
                  <span
                    className={`text-[28px] tracking-tight ${stat.color}`}
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 800,
                    }}
                  >
                    {stat.value}
                  </span>
                  <span
                    className="text-[12px] text-slate-500 leading-tight"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {stat.label}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Column — Hero image with floating badges */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, x: 30 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            {/* Background glow */}
            <div className="absolute inset-8 rounded-3xl bg-gradient-to-br from-violet-400/20 via-cyan-400/10 to-orange-400/20 blur-3xl" />

            {/* Main image container */}
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] max-h-[580px] shadow-2xl shadow-violet-900/15">
              <img
                src={heroImage}
                alt="Confident student"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
            </div>

            {/* Floating badges */}
            {floatingBadges.map((badge, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.5 + i * 0.15,
                }}
                className={`absolute ${badge.position} flex items-center gap-2 px-3.5 py-2 rounded-full border bg-white shadow-lg text-[12px] whitespace-nowrap`}
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                }}
              >
                <span
                  className={`flex items-center gap-1.5 ${badge.color.split(" ").slice(1).join(" ")}`}
                >
                  {badge.icon}
                  {badge.text}
                </span>
              </motion.div>
            ))}

            {/* Decorative accent shapes */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-3xl bg-gradient-to-br from-orange-400 to-pink-500 opacity-30 blur-xl" />
            <div className="absolute -top-4 -left-4 w-20 h-20 rounded-full bg-gradient-to-br from-violet-400 to-cyan-400 opacity-30 blur-xl" />
          </motion.div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span
          className="text-[11px] text-slate-400 uppercase tracking-widest"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
          }}
        >
          Scroll
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-slate-300 to-transparent" />
      </motion.div>
    </section>
  );
}
